import React, { useEffect, useState } from 'react';
import {
  FormStyle,
  FormDiv,
  FormContent,
  LoginFormStyle,
  LoginButtonStyle,
  SignUpButtonRow,
  FooterStyle,
  FooterText
} from './styledComponents';
import { Form, Row, Col, notification, Spin } from 'antd';
import FloatLabel from '../CustomComponents/FloatingLabel';
import { resetPassword, verifyResetToken } from '../../Apis/Auth';
import { useParams, useHistory } from 'react-router-dom';
import CustomTypography from '../CustomComponents/CustomTypography';
import { Link } from "react-router-dom";
import PasswordStrengthIndicator from '../CustomComponents/PasswordStrengthIndicator';

const ResetPasswordForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState('verifying');
  const [expiredMessage, setExpiredMessage] = useState('');
  const history = useHistory();
  const { token } = useParams();
  const passwordValue = Form.useWatch('password', form);

  useEffect(() => {
    verifyResetToken(token)
      .then(response => {
        if (response.data && response.data.success === false) {
          setExpiredMessage(response.data.message || '');
          setTokenStatus('expired');
        } else {
          setTokenStatus('valid');
        }
      })
      .catch(err => {
        setExpiredMessage(
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
            ''
        );
        setTokenStatus('expired');
      });
  }, [token]);

  const SubmitForm = values => {
    setLoading(true);
    const payload = {
      password: values.password,
      token: token
    };
    resetPassword(payload)
      .then(response => {
        if (response.data.success === true) {
          notification['success']({
            message: 'Success',
            description: 'Please use your new password to login.'
          });
          history.push('/login');
          setLoading(false);
        } else {
          notification['error']({
            message: 'Failed',
            description: response.data.message
          });
          setLoading(false);
        }
      })
      .catch(err => {
        setLoading(false);
      });
  };

  const renderContent = () => {
    if (tokenStatus === 'verifying') {
      return (
        <Row justify='center' style={{ padding: '40px 0' }}>
          <Spin size='large' />
        </Row>
      );
    }

    if (tokenStatus === 'expired') {
      return (
        <FormContent>
          <Row justify='center'>
            <Col span={24} style={{ textAlign: 'center' }}>
              <CustomTypography color='#525e6f'>
                This link has expired
              </CustomTypography>
              <p style={{ marginTop: 16, color: '#57657e' }}>
                {expiredMessage ||
                  'The link you used is no longer valid. Please request a new one to reset your password.'}
              </p>
              <Link to='/login'>go to login</Link>
            </Col>
          </Row>
        </FormContent>
      );
    }

    return (
      <>
        <CustomTypography color='#525e6f'>Reset password</CustomTypography>
        <FormContent>
          <Row justify='center'>
            <Col span={24}>
              <LoginFormStyle
                layout='vertical'
                form={form}
                onFinish={SubmitForm}
              >
                <Row gutter={[24, 12]}>
                  <Col span={24}>
                    <Form.Item
                      name='password'
                      rules={[
                        { required: true, message: 'Password is required' },
                        { min: 8, message: 'Password must be at least 8 characters' },
                        {
                          validator(_, value) {
                            if (!value) return Promise.resolve();
                            const missing = [];
                            if (!/[A-Z]/.test(value)) missing.push('uppercase letter');
                            if (!/[a-z]/.test(value)) missing.push('lowercase letter');
                            if (!/[0-9]/.test(value)) missing.push('number');
                            if (!/[^A-Za-z0-9]/.test(value)) missing.push('special character');
                            if (missing.length) return Promise.reject(`Must include: ${missing.join(', ')}`);
                            return Promise.resolve();
                          },
                        },
                      ]}
                      hasFeedback
                    >
                      <FloatLabel label='Password' type='password' />
                    </Form.Item>
                    <PasswordStrengthIndicator value={passwordValue} />
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name='confirm'
                      dependencies={['password']}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: 'Please confirm your password!'
                        },
                        ({ getFieldValue }) => ({
                          validator(rule, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              'The two passwords that you entered do not match!'
                            );
                          }
                        })
                      ]}
                    >
                      <FloatLabel label='Confirm Password' type='password' />
                    </Form.Item>
                  </Col>
                  <SignUpButtonRow>
                    <Col span={12}>
                      <LoginButtonStyle
                        type='primary'
                        htmlType='submit'
                        loading={loading}
                      >
                        Reset
                      </LoginButtonStyle>
                    </Col>
                  </SignUpButtonRow>
                </Row>
              </LoginFormStyle>
            </Col>
          </Row>
        </FormContent>
      </>
    );
  };

  return (
    <FormDiv>
      <FormStyle
        span={16}
        xs={{ span: 14 }}
        sm={{ span: 14 }}
        md={{ span: 10 }}
        lg={{ span: 10 }}
      >
        {renderContent()}
      </FormStyle>
      <FooterStyle>
        <FooterText>
          Dont have an account?<Link to='/signup'> Sign Up</Link>
        </FooterText>
      </FooterStyle>
    </FormDiv>
  );
};

export default ResetPasswordForm;
